import React from 'react';
import { t } from 'i18next';
import { Box, Button, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { useAddServicesAndPrograms } from './hooks';

import { CustomModal, StatusBanner } from '@components';
import { theme } from '@store';
import { paths, ProgramName } from '@common/types';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import { ProgramIcon } from '../../util';

interface IAddServicesAndProgramsProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  disabled: boolean;
}

export const AddServicesAndPrograms = ({
  patient,
  disabled,
}: IAddServicesAndProgramsProps) => {
  const {
    isAddProgramOpen,
    isPending,
    handleCloseAddProgramModal,
    handleOpenAddProgramModal,
    handleEnrollProgram,
    variables,
  } = useAddServicesAndPrograms(patient.id);

  return (
    <>
      {patient.programs.filter(program => program.active).length > 0 ? (
        <Button
          disabled={disabled}
          onClick={handleOpenAddProgramModal}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            minWidth: 'unset',
          }}
        >
          {t('patient.summary.addServicesAndPrograms.button')}
        </Button>
      ) : (
        <Button
          disabled={disabled}
          onClick={handleOpenAddProgramModal}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            minWidth: 'unset',
          }}
          variant='outlined'
        >
          {t('general.ADD')}
        </Button>
      )}

      <CustomModal
        isOpen={isAddProgramOpen && !disabled}
        onClose={handleCloseAddProgramModal}
        isLoading={isPending}
        title={t('patient.summary.addServicesAndPrograms.title')}
      >
        <StatusBanner
          text={t(`patient.summary.addServicesAndPrograms.banner`)}
          variant={'info'}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {[
            patient.programs.map((program, index) => (
              <React.Fragment key={`${program.id} - ${index}`}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <ProgramIcon program={program.name as ProgramName} />
                    <Typography
                      variant='subtitle2'
                      sx={{
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        color: theme.palette.primary.main,
                      }}
                    >
                      {program.name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {program.active && (
                      <Button
                        startIcon={<CheckIcon />}
                        sx={{
                          fontWeight: 'regular',
                          pointerEvents: 'none',
                        }}
                        color='success'
                        variant='outlined'
                      >
                        {t('patient.summary.addServicesAndPrograms.enrolled')}
                      </Button>
                    )}

                    {!program.active && program.can_be_enrolled && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                        }}
                      >
                        {program.was_ever_enrolled && (
                          <Tooltip
                            slotProps={{
                              tooltip: {
                                sx: {
                                  whiteSpace: 'pre-line',
                                },
                              },
                            }}
                            title={
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: 0.2,
                                  typography: 'tooltip',
                                  lineHeight: 1.2,
                                }}
                              >
                                <span>Tier: {program.tier ?? '-'}</span>
                                <span>
                                  Alignment date:{' '}
                                  {program.alignment_date ?? '-'}
                                </span>
                                <span>
                                  Unalignment date:{' '}
                                  {program.unalignment_date ?? '-'}
                                </span>
                                <span>
                                  Reason: {program.status_reason ?? '-'}
                                </span>
                              </Box>
                            }
                            placement='bottom'
                          >
                            <Chip
                              variant='filled'
                              sx={{
                                backgroundColor: theme.palette.lightGrey.main,
                                paddingRight: '32px',
                                position: 'relative',
                                '& .endIcon': {
                                  position: 'absolute',
                                  right: 10,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  pointerEvents: 'none',
                                  fontSize: 20,
                                },
                              }}
                              label={
                                <>
                                  {t(
                                    'patient.summary.addServicesAndPrograms.previouslyEnrolled',
                                  )}
                                  <InfoIcon
                                    className='endIcon'
                                    htmlColor={theme.palette.grey[400]}
                                  />
                                </>
                              }
                            />
                          </Tooltip>
                        )}
                        <Button
                          color='primary'
                          variant='outlined'
                          onClick={() => handleEnrollProgram(program.id)}
                          loading={
                            isPending && variables?.program_id === program.id
                          }
                          disabled={isPending}
                        >
                          {t('patient.summary.addServicesAndPrograms.enroll')}
                        </Button>
                      </Box>
                    )}

                    {!program.active && !program.can_be_enrolled && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                        }}
                      >
                        <Typography
                          variant='caption'
                          sx={{ color: theme.palette.semiBlack.main }}
                        >
                          {program.cannot_be_enrolled_reason}
                        </Typography>
                        <Button color='primary' variant='outlined' disabled>
                          {t('patient.summary.addServicesAndPrograms.enroll')}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
                {index < patient.programs.length - 1 && (
                  <Divider orientation='horizontal' />
                )}
              </React.Fragment>
            )),
          ]}
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            position: 'sticky',
            bottom: 0,
            pb: 3,
            pt: 1,
            backgroundColor: theme.palette.common.white,
            zIndex: 3,
          }}
        >
          <Button
            size='large'
            color='secondary'
            variant='contained'
            sx={{ color: theme.palette.common.white }}
            onClick={handleCloseAddProgramModal}
          >
            {t('general.close')}
          </Button>
        </Box>
      </CustomModal>
    </>
  );
};
